import { Pipe } from '@angular/core';
import type { PipeTransform } from '@angular/core';
import {
    parsePhoneNumberWithError,
    type CountryCode,
} from 'libphonenumber-js/min';
import { isValidPhone } from '@quipex/shared/utils';

export interface PhoneFormatOptions {
    /** Format to use for phone number display */
    format?: 'national' | 'international';
    /** Country code to use when parsing the phone number */
    country?: CountryCode;
}

/**
 * Phone formatting pipe that uses libphonenumber-js for consistent phone number formatting.
 * Supports international and national formatting with country-specific rules.
 * Default country is Australia (AU).
 *
 * @example
 * ```html
 * <!-- Basic usage -->
 * {{ phoneNumber | qpxPhone }}
 *
 * <!-- With specific format -->
 * {{ phoneNumber | qpxPhone:'international' }}
 *
 * <!-- With country context -->
 * {{ phoneNumber | qpxPhone:'national':'AU' }}
 *
 * <!-- With options object -->
 * {{ phoneNumber | qpxPhone:{ format: 'international', country: 'AU' } }}
 * ```
 */
@Pipe({
    name: 'qpxPhone',
})
export class QpxPhonePipe implements PipeTransform {
    transform(
        value: string | null | undefined,
        formatOrOptions?: 'national' | 'international' | PhoneFormatOptions,
        country?: CountryCode
    ): string {
        if (!value || value.trim() === '') {
            return '';
        }

        const trimmedValue = value.trim();

        // Handle options object
        if (typeof formatOrOptions === 'object') {
            return this.formatWithOptions(trimmedValue, formatOrOptions);
        }

        // Handle string format with optional country
        const format = formatOrOptions || 'national';
        const defaultCountry = country || 'AU'; // Default to Australia

        return this.formatWithString(trimmedValue, format, defaultCountry);
    }

    private formatWithOptions(
        value: string,
        options: PhoneFormatOptions
    ): string {
        const country = options.country || 'AU';

        try {
            const phoneNumber = parsePhoneNumberWithError(value, country);
            if (!isValidPhone(value, country)) return value;

            const format = options.format || 'national';
            return format === 'international'
                ? phoneNumber.formatInternational()
                : phoneNumber.formatNational();
        } catch {
            return value;
        }
    }

    private formatWithString(
        value: string,
        format: 'national' | 'international',
        country: CountryCode
    ): string {
        try {
            const phoneNumber = parsePhoneNumberWithError(value, country);
            if (!isValidPhone(value, country)) return value;

            return format === 'international'
                ? phoneNumber.formatInternational()
                : phoneNumber.formatNational();
        } catch {
            return value;
        }
    }
}
